import { DT, IFRAME_TICKS, TILE } from './constants';
import {
  createPlayer,
  createWalker,
  updatePlayer,
  updateWalker,
  type Player,
  type Walker,
} from './entities';
import { isSpike, type Level, type PowerupSpawn } from './level';
import { applyPowerup, defaultMods, type PlayerMods } from './powerups';
import {
  aabbOverlap,
  noInput,
  NoopTelemetry,
  type AABB,
  type DeathCause,
  type InputState,
  type Telemetry,
} from './types';

export type WorldStatus = 'playing' | 'won';

export interface WorldOptions {
  telemetry?: Telemetry;
  /** Starting modifiers (e.g. persistent upgrades bought in the shop). */
  mods?: PlayerMods;
  /** Keys of collectibles already banked in prior runs — shown as un-pickable ghosts. */
  collected?: Iterable<string>;
}

/** A coin/diamond instance with a stable key. `ghost` = already collected in a prior
 *  run (rendered faded, not pickable) so replays can't re-farm currency. */
export interface Collectible {
  x: number;
  y: number;
  key: string;
  ghost: boolean;
}

/**
 * The authoritative simulation for one level. `step(input)` advances exactly one
 * fixed tick deterministically. Holds no DOM/render references — runs in Node so
 * unit and self-play tests can drive it headlessly.
 */
export class World {
  readonly level: Level;
  player: Player;
  walkers: Walker[];
  coins: Collectible[];
  diamonds: Collectible[];
  powerups: PowerupSpawn[];
  mods: PlayerMods;
  status: WorldStatus = 'playing';
  tick = 0;
  deaths = 0;
  coinsCollected = 0;
  diamondsCollected = 0;
  /** Keys of collectibles picked up THIS run (new earnings to bank on win). */
  collectedKeys: string[] = [];

  private readonly telemetry: Telemetry;
  private prev: InputState = noInput();

  constructor(level: Level, opts: WorldOptions = {}) {
    this.level = level;
    this.telemetry = opts.telemetry ?? NoopTelemetry;
    this.mods = opts.mods ?? defaultMods();
    const collected = new Set(opts.collected ?? []);
    this.player = createPlayer(level.spawn);
    this.walkers = this.spawnWalkers();
    this.coins = level.coins.map((c, i) => {
      const key = `${level.id}:c:${i}`;
      return { x: c.x, y: c.y, key, ghost: collected.has(key) };
    });
    this.diamonds = level.diamonds.map((d, i) => {
      const key = `${level.id}:d:${i}`;
      return { x: d.x, y: d.y, key, ghost: collected.has(key) };
    });
    this.powerups = level.powerups.map((p) => ({ ...p }));
    this.telemetry.gameEvent('level_start', { level: level.id });
  }

  /** Elapsed in-sim time in ms (deterministic: ticks × DT). */
  get timeMs(): number {
    return Math.round(this.tick * DT * 1000);
  }

  playerBox(): AABB {
    const b = this.player.body;
    return { x: b.x, y: b.y, w: b.w, h: b.h };
  }

  goalBox(): AABB {
    return { x: this.level.goal.x, y: this.level.goal.y, w: TILE, h: TILE };
  }

  step(input: InputState): void {
    if (this.status === 'won') return;
    this.tick++;

    if (this.player.iframes > 0) this.player.iframes--;

    const jumpPressed = input.jump && !this.prev.jump;
    const jumpReleased = !input.jump && this.prev.jump;
    const dashPressed = input.dash && !this.prev.dash;

    updatePlayer(
      this.player,
      {
        left: input.left,
        right: input.right,
        jumpHeld: input.jump,
        jumpPressed,
        jumpReleased,
        dashPressed,
      },
      this.level,
      this.mods,
    );

    for (const w of this.walkers) updateWalker(w, this.level);

    this.collectItems();

    const cause = this.deathCause();
    if (cause) {
      // Pits always kill. Other hits are absorbed by i-frames, then by armor.
      if (cause === 'pit') {
        this.respawn(cause);
        this.prev = { ...input };
        return;
      }
      if (this.player.iframes <= 0) {
        if (this.mods.armor) {
          this.mods = { ...this.mods, armor: false };
          this.player.iframes = IFRAME_TICKS;
        } else {
          this.respawn(cause);
          this.prev = { ...input };
          return;
        }
      }
    }

    if (aabbOverlap(this.playerBox(), this.goalBox())) {
      this.status = 'won';
      this.telemetry.gameEvent('level_complete', {
        level: this.level.id,
        time_ms: this.timeMs,
        deaths: this.deaths,
      });
    }

    this.prev = { ...input };
  }

  private spawnWalkers(): Walker[] {
    return this.level.enemies.map((e) => createWalker({ x: e.x, y: e.y }, e.speed));
  }

  private collectItems(): void {
    const pb = this.playerBox();
    if (this.coins.length > 0) {
      this.coins = this.coins.filter((c) => {
        if (c.ghost) return true; // already banked — render only, never collect
        const hit = aabbOverlap(pb, { x: c.x, y: c.y, w: TILE, h: TILE });
        if (hit) {
          this.coinsCollected++;
          this.collectedKeys.push(c.key);
        }
        return !hit;
      });
    }
    if (this.diamonds.length > 0) {
      this.diamonds = this.diamonds.filter((d) => {
        if (d.ghost) return true;
        const hit = aabbOverlap(pb, { x: d.x, y: d.y, w: TILE, h: TILE });
        if (hit) {
          this.diamondsCollected++;
          this.collectedKeys.push(d.key);
        }
        return !hit;
      });
    }
    if (this.powerups.length > 0) {
      this.powerups = this.powerups.filter((p) => {
        const hit = aabbOverlap(pb, { x: p.x, y: p.y, w: TILE, h: TILE });
        if (hit) this.mods = applyPowerup(this.mods, p.type);
        return !hit;
      });
    }
  }

  private deathCause(): DeathCause | null {
    const b = this.player.body;

    // Fell out the bottom of the level.
    if (b.y > this.level.height * TILE + TILE) return 'pit';

    // Spikes: any spike tile overlapping the player box.
    const x0 = Math.floor(b.x / TILE);
    const x1 = Math.floor((b.x + b.w - 0.001) / TILE);
    const y0 = Math.floor(b.y / TILE);
    const y1 = Math.floor((b.y + b.h - 0.001) / TILE);
    for (let ty = y0; ty <= y1; ty++) {
      for (let tx = x0; tx <= x1; tx++) {
        if (isSpike(this.level, tx, ty)) return 'spike';
      }
    }

    // Enemies.
    const pb = this.playerBox();
    for (const w of this.walkers) {
      if (aabbOverlap(pb, w.body)) return 'enemy';
    }

    return null;
  }

  private respawn(cause: DeathCause): void {
    this.deaths++;
    this.telemetry.gameEvent('player_death', { level: this.level.id, cause });
    this.player = createPlayer(this.level.spawn);
    this.walkers = this.spawnWalkers();
  }
}
